import React, { useEffect, useState } from 'react';
import { Grid } from '@mui/material';
import { Box, Typography, Card, Checkbox, CssBaseline } from '@mui/joy';
import { useNavigate } from 'react-router-dom';
import TextInput from '../Components/TextInput';
import { extendTheme, ThemeProvider } from '@mui/joy'; // Import from @mui/joy
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faLock } from '@fortawesome/free-solid-svg-icons'; // Import the icons you need
import '@fontsource/montserrat'; // Import Poppins font
import EBNLogo from '../Images/LogoEBNLogin.png';
import coverOrange from '../Images/CoverOrange.png';
import axios from 'axios';
import { API } from '../API/api';
import { useAuth } from '../Context/AuthContext';
import ButtonAttributeJOY from '../Components/ButtonAttributeJOY';
import { CircularProgress } from '@mui/material';
// Create the custom theme with the Poppins font family
const theme = extendTheme({
  typography: {
    fontFamily: 'Montserrat, sans-serif', // Apply Montserrat font family globally
  },

});



export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [errors, setErrors] = useState({
    error: false,
    message: '',
    status: 0
  });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const { login } = useAuth(); // Access login function from AuthContext
  
  const handleSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Validate inputs
    if (!email || !password) {
      setErrors({
        error: true,
        message: 'Veuillez remplir tous les champs',
        status: 400,
      });
      return;
    }

    setLoading(true); // Active le chargement
    setErrors({ error: false, message: '', status: 0 }); // Reset errors

    axios.post(`${API}/login`, { email, password })
      .then(response => {
        console.log('Login response:', response);
        console.log('Login response data:', response.data);
        
        // Handle different response structures
        // La réponse de AuthController retourne directement les données (pas dans .data)
        const responseData = response.data;
        
        // Validate response data
        if (!responseData || responseData.status === false) {
          throw new Error(responseData?.message || 'No data received from server');
        }
        
        // Le token et user sont directement dans responseData (pas dans .data)
        const token = responseData.token || responseData.access_token;
        const user = responseData.user || responseData.data?.user;
        
        if (!token) {
          throw new Error('No token received from server');
        }
        
        if (!user) {
          throw new Error('No user data received from server');
        }

        // Get role - could be in different places
        const role = responseData.role || 
                     user.role?.id || 
                     user.role_id || 
                     responseData.data?.role;

        // Get expiration - could be in different formats
        const expiresAt = responseData.expires_at || 
                         responseData.expiresAt || 
                         responseData.expiration || 
                         (responseData.expires_in ? new Date(Date.now() + responseData.expires_in * 1000).toISOString() : null);

        const newAuthState = {
          isAuthenticated: true,
          token: token,
          user: user,
          expiresAt: expiresAt,
          role: role,
          permissions: responseData.permissions || []
        };

        console.log('Prepared auth state:', {
          hasUser: !!newAuthState.user,
          hasToken: !!newAuthState.token,
          expiresAt: newAuthState.expiresAt,
          role: newAuthState.role,
          userId: newAuthState.user?.id
        });

        try {
          login(newAuthState.user, newAuthState.token, newAuthState.expiresAt, newAuthState.role);
          // Don't set loading to false here - let the navigation happen
        } catch (loginError) {
          console.error('Error in login function:', loginError);
          setErrors({
            error: true,
            message: loginError.message || 'Error during login process',
            status: 500,
          });
          setLoading(false);
        }
      })
      .catch(error => {
        console.error('Login error:', error);
        const status = error.response?.status || 500;
        let message = error.response?.data?.message || error.message || 'An error occurred during login';
        if (status === 403 && !error.response?.data?.message) {
          message = 'Accès refusé (403). Si vous êtes chauffeur, connectez-vous via l\'app mobile. Sinon, le serveur peut bloquer la requête (WAF) — contactez l\'administrateur.';
        }
        setErrors({
          error: true,
          message,
          status,
        });
        setLoading(false);
      });
  };

  // Check if user is already logged in - only run once on mount
  useEffect(() => {
    const storedAuthState = sessionStorage.getItem('authLocalstorage');
    if (!storedAuthState) return;
    
    try {
      const parsed = JSON.parse(storedAuthState);
      if (parsed && parsed.token && parsed.isAuthenticated) {
        // Check if token is not expired
        if (parsed.expiresAt) {
          const expiresAt = new Date(parsed.expiresAt);
          const now = new Date();
          if (!isNaN(expiresAt.getTime()) && expiresAt > now) {
            // User is already logged in with valid token, redirect to dashboard
            console.log('✅ User already logged in, redirecting to Dashboard');
            navigate('/Dashboard', { replace: true });
            return;
          }
        }
      }
    } catch (error) {
      // Invalid stored state, clear it
      console.error('Error parsing stored auth state:', error);
      sessionStorage.removeItem('authLocalstorage');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run once on mount
  return (
    <ThemeProvider theme={theme}> {/* Apply the custom theme with Poppins font */}
      <Grid
        container
        spacing={0}
        sx={{

          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '120vh',
        }}
      >

        <Grid item xs={12} md={6} lg={6} sx={{
          display: { xs: 'none', md: 'flex' }, justifyContent: 'center', alignItems: 'center', height: '125vh',
          backgroundImage: `url(${coverOrange})`, backgroundSize: 'cover', backgroundPosition: 'center', padding: '20px'
        }}>
          <Box sx={{ display: 'grid', justifyContent: 'center', alignItems: 'center' }}>
            <CssBaseline />
          </Box>
        </Grid>
        <Grid item xs={12} md={6} lg={6} xl={6} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '20px' }}>
          <Card sx={{ backgroundColor: 'white', width: '100%', maxWidth: { xs: '100%', sm: 400, md: 500 }, border: 'none', }}>
            <Box
              component="img"
              src={EBNLogo}
              sx={{ width: { xs: 200, md: 200 }, height: 'auto', margin: '0 auto', display: 'block' }}
            />
            <Typography variant="h1" component={'h1'} align="center" sx={{ fontSize: '29px', color: '#3c2151' }}>
              Connectez-vous à votre compte
            </Typography>
            <Typography variant="h2" align="center" sx={{ marginBottom: 2 }}>
              Bienvenue à nouveau ! Veuillez saisir vos coordonnées
            </Typography>
            <Box component="form" onSubmit={handleSubmit}>
              {/* Email Input */}
              <TextInput
                label="Email"
                type="email"
                value={email}
                setValue={setEmail}
                icon={<FontAwesomeIcon icon={faUser} />} // User Icon for Email
              />
              {/* Password Input */}
              <TextInput
                label="Mot de passe"
                type="password"
                value={password}
                setValue={setPassword}
                icon={<FontAwesomeIcon icon={faLock} />} // Lock Icon for Password
              />

              {/* Remember Me Checkbox */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', marginBottom: 2 }}>
                <Checkbox
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  sx={{
                    fontSize: '12px',
                    color: '#3c2151',

                  }}
                  size="sm"
                  label="En vous connectant, vous acceptez les Conditions générales et notre Politique de confidentialité."



                />

              </Box>


              {errors.error && (
                <Typography 
                  variant="h6" 
                  component={'span'} 
                  sx={{ 
                    textAlign: 'center', 
                    fontSize: '12px', 
                    color: 'red', 
                    margin: 2,
                    display: 'block',
                    padding: 1,
                    backgroundColor: '#ffebee',
                    borderRadius: 1
                  }}
                >
                  {errors.message}
                </Typography>
              )}
              {/* Login Button  */}
              <ButtonAttributeJOY
                type="submit"
                Bgcolor="#3c2151"
                color="white"
                bgColorHover="#1a324dde"
                ColorHover="#FFFFFF"
                Label={loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : "Se connecter"}
                height={'56px'}
                hidden={false}
              />

              {/* Forgot Password Link */}

            </Box>
            <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider', textAlign: 'center' }}>
              <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontSize: '0.75rem', mb: 1 }}>
                © Copyright 2026 TMS. Tous droits réservés.
              </Typography>
              <Typography component="a" href="mailto:it.ideumagency@gmail.com" sx={{ fontSize: '0.75rem', color: 'primary.main', textDecoration: 'underline', '&:hover': { color: 'primary.dark' } }}>
                Besoin d'aide ? Contactez le support
              </Typography>
            </Box>
          </Card>
        </Grid>


      </Grid>
    </ThemeProvider>
  );
}
